"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import { Menu, X, Calendar } from "lucide-react";
import { useTranslations } from "next-intl";
import { Link, usePathname } from "@/i18n/navigation";
import LanguageSwitcher from "./LanguageSwitcher";

interface MenuItem {
      id: number;
      title: string;
      url: string;
}

interface NavbarUIProps {
      items: MenuItem[];
      logoUrl: string | null;
      logoAlt?: string | null;
}

export default function NavbarUI({ items, logoUrl, logoAlt }: NavbarUIProps) {
      const [isOpen, setIsOpen] = useState(false);
      const [scrolled, setScrolled] = useState(false);
      const pathname = usePathname();
      const t = useTranslations("common");

      useEffect(() => {
            const handleScroll = () => {
                  setScrolled(window.scrollY > 20);
            };
            window.addEventListener("scroll", handleScroll);
            return () => window.removeEventListener("scroll", handleScroll);
      }, []);

      const isActive = (url: string) => {
            if (url === "/") return pathname === "/";
            return pathname.startsWith(url);
      };

      return (
            <>
                  <header
                        className={`
                              fixed top-0 left-0 right-0 z-50
                              transition-all duration-300 ease-in-out border-b
                              ${scrolled
                                    ? "bg-white shadow-md border-gray-100"
                                    : "bg-white/95 backdrop-blur-md border-transparent"
                              }
                        `}
                  >
                        <nav className={`container mx-auto flex items-center justify-between px-4 md:px-8 transition-all duration-300 ${scrolled ? "py-2" : "py-3"}`}>
                              {/* --- LOGO --- */}
                              <Link href="/" className="relative flex items-center gap-3 group z-50">
                                    {logoUrl ? (
                                          <Image
                                                src={logoUrl}
                                                alt={logoAlt || "Wolistic Center"}
                                                width={240}
                                                height={60}
                                                className={`w-auto object-contain transition-all duration-300 ${scrolled ? 'h-5' : 'h-6'}`}
                                                priority
                                          />
                                    ) : (
                                          <div className="flex flex-col justify-center">
                                                <span className="text-xl font-bold tracking-tight text-gray-900 leading-none">
                                                      Wolistic Center
                                                </span>
                                          </div>
                                    )}
                              </Link>

                              {/* --- DESKTOP MENU --- */}
                              <div className="hidden md:flex items-center gap-8">
                                    <div className="flex items-center gap-6">
                                          {items.map((item) => (
                                                <Link
                                                      key={item.id}
                                                      href={item.url as any}
                                                      className={`
                                                            text-sm font-medium transition-all duration-300 relative group
                                                            ${isActive(item.url) ? "text-[#A68F72]" : "text-gray-600 hover:text-gray-900"}
                                                      `}
                                                >
                                                      {item.title}
                                                      <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-[#A68F72] transition-all duration-300 group-hover:w-full ${isActive(item.url) ? "w-full" : ""}`} />
                                                </Link>
                                          ))}
                                    </div>

                                    <LanguageSwitcher />

                                    <Link
                                          href="/iletisim"
                                          className="
                                                inline-flex items-center gap-2
                                                bg-[#C8486B] text-white
                                                px-5 py-2.5 rounded-xl
                                                text-sm font-semibold
                                                shadow-[0_4px_14px_0_rgba(166,143,114,0.39)]
                                                transition-all duration-300
                                                hover:bg-[#C8486B] hover:shadow-[0_6px_20px_rgba(166,143,114,0.23)] hover:-translate-y-0.5
                                          "
                                    >
                                          <Calendar size={16} />
                                          <span>{t("bookAppointment")}</span>
                                    </Link>
                              </div>

                              {/* --- MOBILE MENU TOGGLE --- */}
                              <button
                                    onClick={() => setIsOpen((prev) => !prev)}
                                    className="relative flex md:hidden h-10 w-10 items-center justify-center rounded-xl bg-gray-50 text-gray-600 hover:bg-[#A68F72] hover:text-white transition-colors duration-300 z-50"
                              >
                                    <span className="sr-only">Menu</span>
                                    {isOpen ? <X size={20} /> : <Menu size={20} />}
                              </button>
                        </nav>
                  </header>

                  {/* --- MOBILE MENU OVERLAY --- */}
                  <div
                        className={`
                              fixed inset-0 z-40 md:hidden
                              transition-all duration-500
                              ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}
                        `}
                  >
                        <div
                              className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity"
                              onClick={() => setIsOpen(false)}
                        />

                        <aside
                              className={`
                                    absolute right-0 top-0 h-full w-[80%] max-w-sm bg-white shadow-2xl
                                    transform transition-transform duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]
                                    ${isOpen ? "translate-x-0" : "translate-x-full"}
                              `}
                        >
                              <div className="flex flex-col h-full pt-24 pb-8 px-6">
                                    <nav className="space-y-2 flex-1">
                                          {items.map((item) => (
                                                <Link
                                                      key={item.id}
                                                      href={item.url as any}
                                                      onClick={() => setIsOpen(false)}
                                                      className={`
                                                            flex items-center justify-between w-full px-4 py-3.5 rounded-2xl text-base font-medium transition-all
                                                            ${isActive(item.url)
                                                                  ? "bg-[#A68F72]/10 text-[#A68F72]"
                                                                  : "text-gray-700 hover:bg-gray-50"
                                                            }
                                                      `}
                                                >
                                                      {item.title}
                                                      {isActive(item.url) && <div className="w-1.5 h-1.5 rounded-full bg-[#A68F72]" />}
                                                </Link>
                                          ))}
                                    </nav>

                                    <div className="mt-auto space-y-6">
                                          <div className="h-px bg-gray-100 w-full" />
                                          <div className="flex justify-center">
                                                <LanguageSwitcher />
                                          </div>
                                          <Link
                                                href="/iletisim"
                                                onClick={() => setIsOpen(false)}
                                                className="
                                                      flex w-full items-center justify-center gap-2 rounded-xl
                                                      bg-[#404041] text-white
                                                      px-4 py-4 text-sm font-semibold
                                                      shadow-lg active:scale-95 transition-transform
                                                "
                                          >
                                                <Calendar size={18} />
                                                {t("bookAppointment")}
                                          </Link>
                                          <p className="text-xs text-center text-gray-400">
                                                Wolistic Center © {new Date().getFullYear()}
                                          </p>
                                    </div>
                              </div>
                        </aside>
                  </div>
            </>
      );
}
